import { Component, inject, signal, DestroyRef, effect, OnInit, ViewChild, ElementRef, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { ContractRestService } from '../../../core/api/contract-rest.service';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Page, Template } from '../../../core/models/template.model';
import { from } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ThemeService } from '../../../core/services/theme.service';
import { MatSelectModule } from '@angular/material/select';
import { ContractService } from '../../../core/services/contract.service';
import { MatDialog, MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { jsPDF } from "jspdf";

@Component({
  selector: 'app-page',
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    EditorModule,
    MatSnackBarModule,
    MatSelectModule,
    MatDialogModule,
    CdkDrag,
    CdkDropList
  ],
  providers: [
    {
      provide: TINYMCE_SCRIPT_SRC,
      useValue: '/tinymce/tinymce.min.js'
    }
  ],
  templateUrl: './page.component.html',
  styleUrl: './page.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class PageComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private contractRestService: ContractRestService = inject(ContractRestService);
  private snackBar = inject(MatSnackBar);
  private destroyRef = inject(DestroyRef);
  private themeService: ThemeService = inject(ThemeService);
  private contractService: ContractService = inject(ContractService);
  @ViewChild('editorContainer', { static: true }) editorContainer!: ElementRef<HTMLDivElement>;

  isSubmitting = signal(false);
  isTemplateSaved = signal(false);

  editorConfig: any;
  html = '';

  showEditor = signal(true);
  pages: any[] = [];

  readonly dialog = inject(MatDialog);

  pageForm: FormGroup = this.fb.group({
    content: ['', [Validators.required]],
    pageId: [''],
    header:[''],
    footer:['']
  });

  footers =[
    {value:null, name: 'Kein'},
    {value:1, name: 'Typ 1'}
  ];

  headers =[
    {value:null, name: 'Kein'},
    {value:1, name: 'Typ 1'},
    {value:2, name: 'Typ 2'},
    {value:3, name: 'Typ 3'}
  ];

  currentHeader: string = '';
  currentFooter: string = '';

  constructor() {
    this.updateEditorConfig();

    effect(() => {
      const isDark = this.themeService.isDarkMode();
      const currentContent = this.pageForm.get('content')?.value || '';
      this.showEditor.set(false);
      this.updateEditorConfig();

      setTimeout(() => {
        this.showEditor.set(true);
        setTimeout(() => {
          if (currentContent) {
            this.pageForm.patchValue({content: currentContent}, {emitEvent: false});
          }
        }, 100);
      }, 50);
    });

  }

  ngOnInit() {
    this.pages = [];
    if (this.contractService.currentTemplate() !== 0) {
      from(this.contractRestService.getTemplateById(this.contractService.currentTemplate()))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: Template) => {
          this.pages = response.pages || [];
          console.log(this.pages);
        },
        error: (error: any) => {
          console.error('❌ Error getting template:', error);
          console.error('❌ Error status:', error.status);
          console.error('❌ Error message:', error.message);
          console.error('❌ Error body:', error.error);

          this.isSubmitting.set(false);

          let errorMessage = 'Fehler beim loading der Vorlage';

          if (error.status === 401) {
            errorMessage = 'Nicht autorisiert. Bitte melden Sie sich erneut an.';
          } else if (error.status === 403) {
            errorMessage = 'Keine Berechtigung zum loading von Vorlagen.';
          } else if (error.status === 400) {
            errorMessage = 'Ungültige Daten. Bitte überprüfen Sie Ihre Eingaben.';
          } else if (error.status === 0) {
            errorMessage = 'Keine Verbindung zum Server. Bitte überprüfen Sie Ihre Internetverbindung.';
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }

          this.snackBar.open(errorMessage, 'Schließen', {
            duration: 5000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
        }
      });
    }

    // try {
    //   // Create the editor state
    //   const state = EditorState.create({
    //     schema: basicSchema,
    //     plugins: exampleSetup({ schema: basicSchema })
    //   });
    //
    //   // Initialize the ProseMirror view
    //   this.view = new EditorView(this.editorContainer?.nativeElement, {
    //     state,
    //     dispatchTransaction: (tr: any) => {
    //       const newState = this.view.state.apply(tr);
    //       this.view.updateState(newState);
    //     }
    //   });
    // } catch (error) {
    //   console.error('Error initializing ProseMirror:', error);
    // }
  }

  private updateEditorConfig() {
    const isDark = this.themeService.isDarkMode();
    this.editorConfig = {
      skin: isDark ? 'oxide-dark' : 'oxide',
      content_css: isDark ? 'dark' : 'default',
      height: 500,
      branding: false,
      menubar: true,
      plugins: [
        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
        'insertdatetime', 'media', 'table', 'help', 'wordcount', 'pagebreak'
        // Premium plugins
      ],
      toolbar: 'undo redo | blocks | pagebreak | ' +
        'bold italic forecolor backcolor | alignleft aligncenter ' +
        'alignright alignjustify | bullist numlist checklist outdent indent | ' +
        'removeformat | table link image | code fullscreen | help',
      content_style: isDark
        ? 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px; background-color: #1e1e1e; color: #e0e0e0; }'
        : 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px; }',
      language: 'de',
      pagebreak_separator: '<!-- page break -->',
      init_instance_callback: (editor: any) => {
        editor.on('PastePreProcess', (e: any) => {
          let text = e.content;

          e.content = e.content
          .replaceAll('&nnbsp;',' ')
          .replaceAll('<!-- [if !supportLists]-->&middot;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <!--[endif]-->','&bull;  ')
          .replaceAll('<!--[if !supportLists]--><span><span>·<span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;\n' +
            '</span></span></span><!--[endif]-->','&bull;  ')
          .replaceAll('<!-- [if !supportLists]-->· <!--[endif]-->', '&bull;  ')
          .replaceAll(/<!--.*?-->/gs, '')
          .replaceAll('&middot;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; ', '&bull;  ')
          .replaceAll('&middot;           ', '&bull;  ')
          .replaceAll(' ',' ')

          const parser = new DOMParser();
          const doc = parser.parseFromString(e.content, "text/html");

          doc.querySelectorAll("[class], [style], [lang]").forEach(el => {
            el.removeAttribute("class");
            el.removeAttribute("id");
            el.removeAttribute("style");
            el.removeAttribute("lang");
          });

          e.content = doc.body.innerHTML
          .replaceAll('<span> </span>', ' ')
          .replaceAll('<o:p>', '<p>')
          .replaceAll('</o:p>', '</p>')

          console.log(e.content);
        });
      }
    };
  }

  addPage() {
    if (this.pageForm.touched) {
      alert('form has content')
    }
    this.pageForm.patchValue({content: '', pageId: '', header:'', footer: ''});
    this.pageForm.markAsUntouched();
  }

  addNewPage(data: any) {
    console.log('📤 Sending page data:', data);

    from(this.contractRestService.createPage(data))
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (response: Page) => {
        console.log('✅ Page created:', response);
        this.isSubmitting.set(false);
        this.pages.push(response);
        this.pageForm.patchValue({pageId: response.id});

        this.snackBar.open('Seite erfolgreich erstellt!', 'Schließen', {
          duration: 4000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['success-snackbar']
        });

        // this.template = response;
        //
        // this.pages = response?.pages || [];

        console.log(response);
        // console.log('📄 Pages after create:', this.pages);
        this.isTemplateSaved.set(true);
      },
      error: (error: any) => {
        console.error('❌ Error creating template:', error);
        console.error('❌ Error status:', error.status);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error body:', error.error);

        this.isSubmitting.set(false);

        let errorMessage = 'Fehler beim Erstellen der Vorlage';

        if (error.status === 401) {
          errorMessage = 'Nicht autorisiert. Bitte melden Sie sich erneut an.';
        } else if (error.status === 403) {
          errorMessage = 'Keine Berechtigung zum Erstellen von Vorlagen.';
        } else if (error.status === 400) {
          errorMessage = 'Ungültige Daten. Bitte überprüfen Sie Ihre Eingaben.';
        } else if (error.status === 0) {
          errorMessage = 'Keine Verbindung zum Server. Bitte überprüfen Sie Ihre Internetverbindung.';
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }

        this.snackBar.open(errorMessage, 'Schließen', {
          duration: 5000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  updatePage(data: any) {
    from(this.contractRestService.updatePage(data))
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (response: Page) => {
        console.log('✅ Page updated:', response);
        this.isSubmitting.set(false);

        this.snackBar.open('Seite erfolgreich aktualisiert!', 'Schließen', {
          duration: 4000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['success-snackbar']
        });

        // this.template = response;
        // ✅ Safe access to pages
        // this.pages = response?.pages || [];
        const ind = this.pages?.findIndex(page => page.id === response.id);

        if (ind > -1 && response !== undefined) {
          const newPage = response;
          this.pages[ind] = newPage;
        }

        console.log('📄 Pages after update:', this.pages);
        // this.isTemplateSaved.set(true);
      },
      error: (error: any) => {
        console.error('❌ Error updating Page:', error);
        this.isSubmitting.set(false);

        let errorMessage = 'Fehler beim Aktualisieren der Page';

        if (error.status === 401) {
          errorMessage = 'Nicht autorisiert. Bitte melden Sie sich erneut an.';
        } else if (error.status === 403) {
          errorMessage = 'Keine Berechtigung zum Aktualisieren von Page.';
        } else if (error.status === 400) {
          errorMessage = 'Ungültige Daten. Bitte überprüfen Sie Ihre Eingaben.';
        } else if (error.status === 0) {
          errorMessage = 'Keine Verbindung zum Server. Bitte überprüfen Sie Ihre Internetverbindung.';
        }

        this.snackBar.open(errorMessage, 'Schließen', {
          duration: 5000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  onPageSubmit(): void {
    if (!this.pageForm.valid) {
      Object.keys(this.pageForm.controls).forEach(key => {
        this.pageForm.get(key)?.markAsTouched();
      });

      this.snackBar.open('Bitte füllen Sie alle Pflichtfelder aus', 'Schließen', {
        duration: 3000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
        panelClass: ['warning-snackbar']
      });

      return;
    }

    this.isSubmitting.set(true);
    if (this.contractService.currentTemplate() === 0) {
      return;
    }

    console.log(this.pageForm.value.content);

    const length = this.contractService.checkPageLength(this.currentHeader, this.currentFooter);
    const textHeight = this.contractService.textHeight(this.pageForm.value.content);

    console.log(textHeight/length);

    let pageData: Page = {
      sort_index: this.pages?.length || 0,
      body_html: this.pageForm.value.content,
      header_html: this.currentHeader,
      footer_html: this.currentFooter,
      template_id: this.contractService.currentTemplate(),
    }

    if (this.pageForm.value.pageId !== '') {
      pageData['id'] = this.pageForm.value.pageId;
      this.updatePage(pageData);
    } else {
      this.addNewPage(pageData);
    }
    this.pageForm.markAsUntouched();
  }

  editPage(page: Page) {
    console.log(page);
    // this.currentHeader = page.header_html;
    // this.currentFooter = page.footer_html;
    this.pageForm.patchValue({content: page.body_html, pageId: page.id, header: page.header_html, footer: page.footer_html})
  }

  async deletePage(id: number): Promise<void> {
    try {
      await this.contractRestService.deletePage(id);
      this.pages = this.pages?.filter(page => page.id !== id);
      if (this.pageForm.value.pageId === id) {
        this.pageForm.patchValue({pageId: ''});
      }
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  }

  preview(page: any) {
    let dialogRef = this.dialog.open(PreviewPopup, {
      data: page,
      height: '794px',
      width: '794px',
      maxWidth: 794,
      maxHeight: 1123
    });
  }

  async addFirstPage() {
    try {
      from(this.contractRestService.loadTemplate('first-page.template.html'))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (html: string) => {

          var page = {
            header_html: '',
            footer_html: '',
            template_id: this.contractService.currentTemplate(),
            sort_index: 0,
            body_html: html
          }


          var sorted: any[] = [];
          this.pages.forEach((page, index) => {
            sorted.push({id: page.id, sort_index: index+1});
          });
          this.contractRestService.sortPages(sorted);
          this.pages?.unshift(page);

          this.onPageSubmit();
        }
      });
    } catch(error) {
      console.log('could not open the file');
    }
  }

  async addHeader($type:any) {
    const type = $type.value;

    if (type === null){
      this.currentHeader='';
      return;
    }
    try {
      from(this.contractRestService.loadTemplate('header-' + type + '.template.html'))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (html: string) => {
          this.currentHeader = html;
        }
      });
    } catch(error) {
      console.log('could not open the file');
    }
  }

  async addFooter($type:any) {
    const type = $type.value;

    if (type === null){
      this.currentFooter = '';
      return;
    }
    try {
      from(this.contractRestService.loadTemplate('footer-' + type + '.template.html'))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (html: string) => {
          this.currentFooter = html;
        }
      });
    } catch(error) {
      console.log('could not open the file');
    }
  }

  async drop(event: any) {
    console.log('drop it, drooop iiit');
    moveItemInArray(this.pages, event.previousIndex, event.currentIndex);
    var sorted: any[] = [];
    this.pages.forEach((page, index) => {
      console.log(index);
      sorted.push({id: page.id, sort_index: index});
    });

    await this.contractRestService.sortPages(sorted);
  }


  ngOnDestroy(): void {
    // Properly destroy the editor to avoid memory leaks
    // if (this.view) {
    //   this.view.destroy();
    // }
  }

  addSignaturePage() {
    try {
      from(this.contractRestService.loadTemplate('signature-page.template.html'))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (html: string) => {
          this.pageForm.patchValue({content: html});

          var page = {
            header_html: '',
            footer_html: '',
            template_id: this.contractService.currentTemplate(),
            sort_index: this.pages.length,
            body_html: html
          }


          var sorted: any[] = [];
          this.pages.forEach((page, index) => {
            sorted.push({id: page.id, sort_index: index});
          });
          this.contractRestService.sortPages(sorted);

          this.onPageSubmit();
        }
      });
    } catch(error) {
      console.log('could not open the file');
    }
  }
}

@Component({
  selector: 'app-page',
  imports: [
    MatDialogModule
  ],
  providers: [
    {
      provide: TINYMCE_SCRIPT_SRC,
      useValue: '/tinymce/tinymce.min.js'
    }
  ],
  templateUrl: './page-preview.component.html',
  styleUrl: './page-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class PreviewPopup {
  data = inject(MAT_DIALOG_DATA);
}
